import React, { useEffect, useMemo, useState, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import { MessageSquare, Plus, ArrowLeft, User, Clock, Heart, Pencil, Check, X } from "lucide-react";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import AdSlot from "../components/AdSlot";
import Feed from "./Feed";

const timeAgo = (iso) => {
  const d = new Date(iso);
  const diff = (Date.now() - d.getTime()) / 1000;
  if (diff < 60) return "just now";
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return d.toLocaleDateString();
};

const AuthorLink = ({ name }) => (
  <Link to={`/u/${encodeURIComponent(name)}`} className="flex items-center gap-1 text-[#e0a82e] hover:underline">
    <User className="h-3 w-3" /> {name}
  </Link>
);

const Community = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [categories, setCategories] = useState([]);
  const [topics, setTopics] = useState([]);
  const [view, setView] = useState({ mode: "list" });
  const [activeCat, setActiveCat] = useState("all");
  const [newTopic, setNewTopic] = useState({ title: "", category: "discussion", body: "" });
  const [replyText, setReplyText] = useState("");
  const [detail, setDetail] = useState({ topic: null, replies: [] });
  const [loading, setLoading] = useState(false);
  const [posting, setPosting] = useState(false);
  const [editTopic, setEditTopic] = useState(null); // {title, body}
  const [editReplyId, setEditReplyId] = useState(null);
  const [editReplyText, setEditReplyText] = useState("");
  const [tab, setTab] = useState("feed");

  const loadTopics = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/topics`, { params: { category: activeCat } });
      setTopics(data);
    } finally {
      setLoading(false);
    }
  }, [activeCat]);

  useEffect(() => {
    api.get("/categories").then(({ data }) => {
      setCategories(data);
      if (data.length) setNewTopic((n) => ({ ...n, category: data[0].id }));
    });
  }, []);

  useEffect(() => {
    if (view.mode === "list") loadTopics();
  }, [view.mode, loadTopics]);

  const catName = useMemo(
    () => (id) => categories.find((c) => c.id === id)?.name || id,
    [categories]
  );

  const requireAuth = (cb) => {
    if (!user) {
      navigate("/login", { state: { from: "/community" } });
      return;
    }
    cb();
  };

  const openTopic = async (id) => {
    const { data } = await api.get(`/topics/${id}`);
    setDetail(data);
    setEditTopic(null);
    setEditReplyId(null);
    setView({ mode: "detail", id });
  };

  const createTopic = async (e) => {
    e.preventDefault();
    setPosting(true);
    try {
      const { data } = await api.post("/topics", newTopic);
      setNewTopic({ title: "", category: categories[0]?.id || "discussion", body: "" });
      setDetail({ topic: data, replies: [] });
      setView({ mode: "detail", id: data.id });
    } catch (err) {
      alert(err?.response?.data?.detail || "Could not post topic.");
    } finally {
      setPosting(false);
    }
  };

  const addReply = async (topicId) => {
    if (!replyText.trim()) return;
    setPosting(true);
    try {
      const { data } = await api.post(`/topics/${topicId}/replies`, { body: replyText });
      setDetail((d) => ({ ...d, replies: [...d.replies, data] }));
      setReplyText("");
    } catch (err) {
      alert(err?.response?.data?.detail || "Could not post reply.");
    } finally {
      setPosting(false);
    }
  };

  const likeTopic = async (topicId) => {
    if (!user) return navigate("/login", { state: { from: "/community" } });
    const { data } = await api.post(`/topics/${topicId}/like`);
    setDetail((d) => ({ ...d, topic: { ...d.topic, likes: data.likes, likedByMe: data.likedByMe } }));
  };

  const likeReply = async (replyId) => {
    if (!user) return navigate("/login", { state: { from: "/community" } });
    const { data } = await api.post(`/replies/${replyId}/like`);
    setDetail((d) => ({
      ...d,
      replies: d.replies.map((r) => (r.id === replyId ? { ...r, likes: data.likes, likedByMe: data.likedByMe } : r)),
    }));
  };

  const saveTopicEdit = async () => {
    const { data } = await api.put(`/topics/${detail.topic.id}`, {
      title: editTopic.title,
      body: editTopic.body,
      category: detail.topic.category,
    });
    setDetail((d) => ({ ...d, topic: { ...d.topic, ...data } }));
    setEditTopic(null);
  };

  const saveReplyEdit = async (replyId) => {
    const { data } = await api.put(`/replies/${replyId}`, { body: editReplyText });
    setDetail((d) => ({ ...d, replies: d.replies.map((r) => (r.id === replyId ? data : r)) }));
    setEditReplyId(null);
  };

  const activeTopic = detail.topic;
  const isMine = (authorId) => user && authorId === user.id;

  const LikeBtn = ({ liked, count, onClick }) => (
    <button
      onClick={onClick}
      className={`flex items-center gap-1 rounded px-3 py-1.5 text-sm transition-colors ${
        liked ? "bg-[#e0574c]/20 text-[#e0574c]" : "bg-[#333] text-[#c0c0c0] hover:text-[#e0574c]"
      }`}
    >
      <Heart className={`h-4 w-4 ${liked ? "fill-[#e0574c]" : ""}`} /> {count}
    </button>
  );

  return (
    <div className="mx-auto max-w-5xl px-4 py-12 md:px-8">
      <AdSlot slot="community-top" />
      <div className="mt-4">
        <h1 className="font-display text-4xl font-700 uppercase tracking-wide text-[#e0a82e]">Community</h1>
        <p className="mt-1 text-sm text-[#b0b0b0]">Share. Connect. Support.</p>
        <div className="mt-4 flex gap-2 border-b border-white/10">
          {[
            { id: "feed", label: "Community Feed" },
            { id: "forum", label: "Forum" },
          ].map((t) => (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              className={`-mb-px border-b-2 px-4 py-3 font-display text-sm font-600 uppercase tracking-wide transition-colors ${
                tab === t.id
                  ? "border-[#e0a82e] text-[#e0a82e]"
                  : "border-transparent text-[#c0c0c0] hover:text-[#e0a82e]"
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>
      </div>

      {tab === "feed" && <Feed />}

      {tab === "forum" && (
      <>
      <div className="mt-6 flex flex-col items-start justify-between gap-4 border-b border-white/10 pb-6 sm:flex-row sm:items-center">
        <div>
          <h2 className="font-display text-2xl font-700 uppercase tracking-wide text-[#f0f0f0]">Community Forum</h2>
          <p className="mt-1 text-sm text-[#b0b0b0]">Start a topic or join a discussion.</p>
        </div>
        {view.mode === "list" && (
          <button
            onClick={() => requireAuth(() => setView({ mode: "new" }))}
            className="inline-flex items-center gap-2 rounded bg-[#e0a82e] px-5 py-2.5 font-display text-sm font-600 uppercase tracking-wide text-[#1c1c1c] transition-colors hover:bg-[#f2b93f]"
          >
            <Plus className="h-4 w-4" /> New Topic
          </button>
        )}
      </div>

      {/* LIST VIEW */}
      {view.mode === "list" && (
        <div className="mt-6">
          <div className="mb-6 flex flex-wrap gap-2">
            {[{ id: "all", name: "All" }, ...categories].map((c) => (
              <button
                key={c.id}
                onClick={() => setActiveCat(c.id)}
                className={`rounded-full border px-4 py-1.5 font-display text-sm uppercase tracking-wide transition-colors ${
                  activeCat === c.id
                    ? "border-[#e0a82e] bg-[#e0a82e] text-[#1c1c1c]"
                    : "border-white/15 text-[#c0c0c0] hover:border-[#e0a82e] hover:text-[#e0a82e]"
                }`}
              >
                {c.name}
              </button>
            ))}
          </div>

          {loading ? (
            <p className="py-10 text-center text-[#9a9a9a]">Loading topics...</p>
          ) : (
            <div className="space-y-3">
              {topics.length === 0 && (
                <p className="py-10 text-center text-[#9a9a9a]">No topics yet. Be the first to post.</p>
              )}
              {topics.map((t) => (
                <button
                  key={t.id}
                  onClick={() => openTopic(t.id)}
                  className="block w-full rounded-md border border-white/10 bg-[#242424] p-5 text-left transition-all hover:border-[#e0a82e]/50 hover:bg-[#2b2b2b]"
                >
                  <div className="flex items-start justify-between gap-4">
                    <div>
                      <span className="font-display text-xs uppercase tracking-widest text-[#e0574c]">{catName(t.category)}</span>
                      <h3 className="mt-1 font-display text-xl font-500 text-[#f0f0f0]">{t.title}</h3>
                      <p className="mt-1 line-clamp-1 text-sm text-[#a8a8a8]">{t.body}</p>
                      <div className="mt-3 flex items-center gap-4 text-xs text-[#8f8f8f]">
                        <span className="flex items-center gap-1"><User className="h-3 w-3" /> {t.author}</span>
                        <span className="flex items-center gap-1"><Clock className="h-3 w-3" /> {timeAgo(t.createdAt)}</span>
                      </div>
                    </div>
                    <div className="flex shrink-0 flex-col items-end gap-2">
                      <span className="flex items-center gap-1 rounded bg-[#333] px-3 py-1.5 text-sm text-[#e0a82e]">
                        <MessageSquare className="h-4 w-4" /> {t.replies}
                      </span>
                      <span className="flex items-center gap-1 rounded bg-[#333] px-3 py-1.5 text-sm text-[#e0574c]">
                        <Heart className={`h-4 w-4 ${t.likedByMe ? "fill-[#e0574c]" : ""}`} /> {t.likes}
                      </span>
                    </div>
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>
      )}

      {/* NEW TOPIC VIEW */}
      {view.mode === "new" && (
        <div className="mt-6">
          <button onClick={() => setView({ mode: "list" })} className="mb-6 inline-flex items-center gap-2 font-display text-sm uppercase text-[#e0574c] hover:text-[#e0a82e]">
            <ArrowLeft className="h-4 w-4" /> Back to Forum
          </button>
          <form onSubmit={createTopic} className="space-y-4 rounded-md border border-white/10 bg-[#242424] p-6">
            <div>
              <label className="mb-1 block font-display text-sm uppercase text-[#d0d0d0]">Category</label>
              <select
                value={newTopic.category}
                onChange={(e) => setNewTopic({ ...newTopic, category: e.target.value })}
                className="w-full rounded border border-white/15 bg-[#1e1e1e] px-4 py-2.5 text-[#eee] outline-none focus:border-[#e0a82e]"
              >
                {categories.map((c) => (
                  <option key={c.id} value={c.id}>{c.name}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="mb-1 block font-display text-sm uppercase text-[#d0d0d0]">Title</label>
              <input required value={newTopic.title} onChange={(e) => setNewTopic({ ...newTopic, title: e.target.value })}
                className="w-full rounded border border-white/15 bg-[#1e1e1e] px-4 py-2.5 text-[#eee] outline-none focus:border-[#e0a82e]" />
            </div>
            <div>
              <label className="mb-1 block font-display text-sm uppercase text-[#d0d0d0]">Message</label>
              <textarea required rows={5} value={newTopic.body} onChange={(e) => setNewTopic({ ...newTopic, body: e.target.value })}
                className="w-full rounded border border-white/15 bg-[#1e1e1e] px-4 py-2.5 text-[#eee] outline-none focus:border-[#e0a82e]" />
            </div>
            <button type="submit" disabled={posting} className="rounded bg-[#e0a82e] px-6 py-2.5 font-display text-sm font-600 uppercase tracking-wide text-[#1c1c1c] transition-colors hover:bg-[#f2b93f] disabled:opacity-60">
              {posting ? "Posting..." : "Post Topic"}
            </button>
          </form>
        </div>
      )}

      {/* DETAIL VIEW */}
      {view.mode === "detail" && activeTopic && (
        <div className="mt-6">
          <button onClick={() => setView({ mode: "list" })} className="mb-6 inline-flex items-center gap-2 font-display text-sm uppercase text-[#e0574c] hover:text-[#e0a82e]">
            <ArrowLeft className="h-4 w-4" /> Back to Forum
          </button>
          <div className="rounded-md border border-white/10 bg-[#242424] p-6">
            <span className="font-display text-xs uppercase tracking-widest text-[#e0574c]">{catName(activeTopic.category)}</span>
            {editTopic ? (
              <div className="mt-2 space-y-3">
                <input value={editTopic.title} onChange={(e) => setEditTopic({ ...editTopic, title: e.target.value })}
                  className="w-full rounded border border-white/15 bg-[#1e1e1e] px-4 py-2.5 text-[#eee] outline-none focus:border-[#e0a82e]" />
                <textarea rows={5} value={editTopic.body} onChange={(e) => setEditTopic({ ...editTopic, body: e.target.value })}
                  className="w-full rounded border border-white/15 bg-[#1e1e1e] px-4 py-2.5 text-[#eee] outline-none focus:border-[#e0a82e]" />
                <div className="flex gap-2">
                  <button onClick={saveTopicEdit} className="inline-flex items-center gap-1 rounded bg-[#e0a82e] px-4 py-2 font-display text-sm uppercase text-[#1c1c1c] hover:bg-[#f2b93f]"><Check className="h-4 w-4" /> Save</button>
                  <button onClick={() => setEditTopic(null)} className="inline-flex items-center gap-1 rounded border border-white/15 px-4 py-2 font-display text-sm uppercase text-[#c0c0c0] hover:text-white"><X className="h-4 w-4" /> Cancel</button>
                </div>
              </div>
            ) : (
              <>
                <h2 className="mt-1 font-display text-2xl font-600 text-[#f0f0f0] md:text-3xl">{activeTopic.title}</h2>
                <div className="mt-2 flex flex-wrap items-center gap-4 text-xs text-[#8f8f8f]">
                  <AuthorLink name={activeTopic.author} />
                  <span className="flex items-center gap-1"><Clock className="h-3 w-3" /> {timeAgo(activeTopic.createdAt)}</span>
                  {activeTopic.editedAt && <span className="italic">(edited)</span>}
                </div>
                <p className="mt-4 whitespace-pre-wrap leading-relaxed text-[#d0d0d0]">{activeTopic.body}</p>
                <div className="mt-4 flex items-center gap-3">
                  <LikeBtn liked={activeTopic.likedByMe} count={activeTopic.likes} onClick={() => likeTopic(activeTopic.id)} />
                  {isMine(activeTopic.author_id) && (
                    <button onClick={() => setEditTopic({ title: activeTopic.title, body: activeTopic.body })}
                      className="flex items-center gap-1 rounded bg-[#333] px-3 py-1.5 text-sm text-[#c0c0c0] transition-colors hover:text-[#e0a82e]">
                      <Pencil className="h-4 w-4" /> Edit
                    </button>
                  )}
                </div>
              </>
            )}
          </div>

          <h3 className="mt-8 font-display text-lg uppercase tracking-wide text-[#e0a82e]">{detail.replies.length} Replies</h3>
          <div className="mt-4 space-y-3">
            {detail.replies.map((r) => (
              <div key={r.id} className="rounded-md border border-white/10 bg-[#1f1f1f] p-4">
                <div className="flex items-center gap-3 text-xs text-[#8f8f8f]">
                  <AuthorLink name={r.author} />
                  <span>{timeAgo(r.createdAt)}</span>
                  {r.editedAt && <span className="italic">(edited)</span>}
                </div>
                {editReplyId === r.id ? (
                  <div className="mt-2 space-y-2">
                    <textarea rows={3} value={editReplyText} onChange={(e) => setEditReplyText(e.target.value)}
                      className="w-full rounded border border-white/15 bg-[#1e1e1e] px-4 py-2.5 text-[#eee] outline-none focus:border-[#e0a82e]" />
                    <div className="flex gap-2">
                      <button onClick={() => saveReplyEdit(r.id)} className="inline-flex items-center gap-1 rounded bg-[#e0a82e] px-3 py-1.5 text-sm text-[#1c1c1c] hover:bg-[#f2b93f]"><Check className="h-4 w-4" /> Save</button>
                      <button onClick={() => setEditReplyId(null)} className="inline-flex items-center gap-1 rounded border border-white/15 px-3 py-1.5 text-sm text-[#c0c0c0]"><X className="h-4 w-4" /> Cancel</button>
                    </div>
                  </div>
                ) : (
                  <>
                    <p className="mt-2 whitespace-pre-wrap text-sm leading-relaxed text-[#d0d0d0]">{r.body}</p>
                    <div className="mt-3 flex items-center gap-3">
                      <LikeBtn liked={r.likedByMe} count={r.likes} onClick={() => likeReply(r.id)} />
                      {isMine(r.author_id) && (
                        <button onClick={() => { setEditReplyId(r.id); setEditReplyText(r.body); }}
                          className="flex items-center gap-1 rounded bg-[#333] px-3 py-1.5 text-sm text-[#c0c0c0] transition-colors hover:text-[#e0a82e]">
                          <Pencil className="h-4 w-4" /> Edit
                        </button>
                      )}
                    </div>
                  </>
                )}
              </div>
            ))}
          </div>

          <div className="mt-6">
            {user ? (
              <div className="rounded-md border border-white/10 bg-[#242424] p-4">
                <textarea rows={3} value={replyText} onChange={(e) => setReplyText(e.target.value)} placeholder="Write a reply..."
                  className="w-full rounded border border-white/15 bg-[#1e1e1e] px-4 py-2.5 text-[#eee] outline-none focus:border-[#e0a82e]" />
                <button onClick={() => addReply(activeTopic.id)} disabled={posting}
                  className="mt-3 rounded bg-[#e0a82e] px-5 py-2 font-display text-sm font-600 uppercase tracking-wide text-[#1c1c1c] transition-colors hover:bg-[#f2b93f] disabled:opacity-60">
                  {posting ? "Posting..." : "Post Reply"}
                </button>
              </div>
            ) : (
              <p className="rounded-md border border-white/10 bg-[#242424] p-4 text-sm text-[#b0b0b0]">
                <Link to="/login" state={{ from: "/community" }} className="font-display uppercase text-[#e0a82e]">Log in</Link> or{" "}
                <Link to="/register" className="font-display uppercase text-[#e0a82e]">register</Link> to join the conversation.
              </p>
            )}
          </div>
        </div>
      )}
      </>
      )}
    </div>
  );
};

export default Community;
